import axios from "axios";
import { Exclusion, PlayerColor, Position, Settings } from "./models";
import _ from "lodash";
import { readStream } from "./ndjson";
import { Chess } from "chess.js";
import { useStorage } from "./ZustandState";

const weightedRandom = (options: ModifiedLichessMove[]) => {
    let i;
    const weights: number[] = [];

    for (i = 0; i < options.length; i++) {
        weights[i] = options[i].total + (weights[i - 1] || 0);
    }

    const random = Math.random() * weights[weights.length - 1];

    for (i = 0; i < weights.length; i++) if (weights[i] > random) break;

    return options[i];
};

interface LichessMove {
    averageRating: number;
    white: number;
    black: number;
    draws: number;
    game: unknown;
    san: string;
    uci: string;
}

export interface LichessResponse {
    white: number;
    draws: number;
    black: number;
    moves: LichessMove[];
    recentGames: RecentGame[];
    topGames: any[];
    opening: Opening | null;
    isPlayerData?: boolean;
}

interface Opening {
    eco: string;
    name: string;
}

interface RecentGame {
    uci: string;
    id: string;
    winner: string;
    speed: string;
    mode: string;
    black: Black;
    white: Black;
    year: number;
    month: string;
}

interface Black {
    name: string;
    rating: number;
}

const convertMonthToNumber = (month: string) => {
    const monthMap: { [key: string]: string } = {
        January: "01",
        February: "02",
        March: "03",
        April: "04",
        May: "05",
        June: "06",
        July: "07",
        August: "08",
        September: "09",
        October: "10",
        November: "11",
        December: "12",
    };
    return monthMap[month];
};

const getOpeningDataFromPlayer = async (
    fen: string,
    ratings: string[],
    settings: Settings,
    playerColor?: PlayerColor,
    selectedPosition?: Position,
) => {
    // Format: "2021-01"
    const since = `${selectedPosition?.sinceYear}-${convertMonthToNumber(selectedPosition?.sinceMonth || "")}`;
    // const until = `${selectedPosition?.untilYear}-${convertMonthToNumber(selectedPosition?.untilMonth)}`;

    const urlParams = {
        fen: fen,
        moves: "12",
        speeds: selectedPosition?.timeControls?.join(",") || "",
        // ratings: ratings?.join(","),
        player: selectedPosition?.lichessPlayer!,
        color: playerColor === "w" ? "black" : "white",
        since,
        // until,
    };

    const stream = fetch(`https://explorer.lichess.ovh/player?` + new URLSearchParams(urlParams).toString());

    const lawl = new Promise((resolve, reject) => {
        stream
            .then(
                readStream((obj: any) => {
                    resolve(obj);
                }),
            )
            .catch((error) => {
                reject(error);
            });
    });

    const response: any = await lawl;
    console.log(response);
    return response;
};

export const getLichessOpeningData = async (
    game: Chess,
    ratings?: string[],
    settings?: Settings,
    playerColor?: PlayerColor,
    selectedPosition?: Position,
): Promise<LichessResponse> => {
    const fen = game.fen();
    const turn = game.turn();
    const unsub = useStorage.subscribe((state) => state);
    const uniquePositionAgainstPlayer = useStorage.getState().uniquePositionAgainstPlayer;
    const setUniquePositionAgainstPlayer = useStorage.getState().setUniquePositionAgainstPlayer;

    if (selectedPosition?.engineSource === "lichessPlayer" && playerColor !== turn && !uniquePositionAgainstPlayer) {
        const dataAgainstPlayer = await getOpeningDataFromPlayer(
            fen,
            ratings!,
            settings,
            playerColor,
            selectedPosition,
        );
        const total = dataAgainstPlayer.white + dataAgainstPlayer.black + dataAgainstPlayer.draws;
        if (total > 0) {
            return { ...dataAgainstPlayer, isPlayerData: true };
        } else {
            setUniquePositionAgainstPlayer(true);
        }
    }

    if (selectedPosition?.engineSource === "lichessPlayer" && playerColor === turn && !uniquePositionAgainstPlayer) {
        return { isPlayerData: true } as LichessResponse;
    }

    unsub();

    const response = await axios.post(`${import.meta.env.VITE_OPENING_TRAINER_BACKEND_URL}/ot_lichess_explorer`, {
        fen: fen,
        moves: 12,
        speeds: selectedPosition?.timeControls?.join(","),
        ratings: ratings?.join(","),
        player: selectedPosition?.lichessPlayer,
        color: playerColor === "w" ? "black" : "white",
        type: _.first(ratings) === "Masters" ? "masters" : "lichess",
    });

    return response.data;
};

export interface ModifiedLichessMove {
    san: string;
    winRatio: number;
    black: number;
    white: number;
    total: number;
    averageRating: number;
}

const modifyListToPreferDubious = (list: ModifiedLichessMove[], totalOverall: number) => {
    return list.map((move) => {
        // Only those that are played at least 1 in 150 games
        if (move.total / totalOverall < 0.0075) {
            return move;
        }

        // Only those that have a win ratio a bit higher than the first move and played less than half as much and have a total of at least 1000
        const firstMove = list[0];
        const WIN_RATIO_THRESHOLD = 0.02;
        const TOTAL_THRESHOLD = 1000;
        if (
            move.winRatio - firstMove.winRatio < WIN_RATIO_THRESHOLD ||
            move.total > firstMove.total / 2 ||
            move.total < TOTAL_THRESHOLD
        ) {
            return move;
        }

        // The remaining moves are dubious. Set their total equal to the first move
        // console.log("Dubious move:", move.san);
        return {
            ...move,
            total: firstMove.total,
        };
    });
};

export const getOpeningBookMove = (
    data: LichessResponse,
    rejectionThreshold: number,
    playerColor: PlayerColor,
    preferDubious: boolean,
    exclusions?: Exclusion[],
) => {
    if (data.moves.length === 0) {
        console.log("Found no moves from opening book");
        return;
    }

    if (exclusions) {
        const excludedMoves = exclusions?.map((exclusion) => exclusion.move);
        data.moves = data.moves.filter((move) => {
            return !excludedMoves?.includes(move.san);
        });
    }

    const topList = data.moves.map((move: LichessMove) => {
        const total = move.white + move.black + move.draws;
        return {
            san: move.san,
            winRatio: playerColor === "w" ? move.black / total : move.white / total,
            black: move.black,
            white: move.white,
            total,
            averageRating: move.averageRating,
        };
    });

    const totalOverall = data.white + data.black + data.draws;
    let fileterdTopList = topList.filter((move) => {
        if (move.total / totalOverall < rejectionThreshold / 100) {
            return false;
        }
        return true;
    });
    if (fileterdTopList.length === 0) {
        fileterdTopList = [topList[0]];
    }

    if (preferDubious) {
        fileterdTopList = modifyListToPreferDubious(fileterdTopList, totalOverall);
    }

    const pickedMove = weightedRandom(fileterdTopList);

    return pickedMove;
};
